import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, DatePicker, Empty, Tag, Tooltip } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage, deviceTimeZone } from "../api/client";
import { useAuthStore } from "../auth/store";
import { PageHeader } from "../components/PageHeader";

interface Slot {
  id: string;
  document_type: string;
  document_id: string | null;
  document_number: string | null;
  planned_from: string;
  planned_to: string;
  vehicle: string | null;
  driver: string | null;
  status: string;
}
interface Schedule {
  date: string;
  /** `number` — the gate's number on its dock (3D docks, 29.10.2026); null when it stands on no dock. */
  gates: { id: string; code: string; number?: string | null; slots: Slot[] }[];
}

const STATUS: Record<string, { label: string; color: string }> = {
  planned: { label: "план", color: "blue" },
  arrived: { label: "прибыл", color: "orange" },
  in_progress: { label: "обработка", color: "purple" },
  done: { label: "готово", color: "green" },
  cancelled: { label: "отменён", color: "default" },
};
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/** Dock schedule for a day: one row per gate, slots drawn on a 24-hour ruler (ФТ-5.2, 6.2). */
export function DockSchedulePage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const docks = useAuthStore((s) => s.option("use_docks"));
  const [day, setDay] = useState<Dayjs>(dayjs());
  const iso = day.format("YYYY-MM-DD");
  const schedule = useQuery({ queryKey: ["dock-schedule", iso], queryFn: () => api.get<Schedule>("/dock-slots/schedule", { date: iso }), refetchInterval: 60_000 });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) => api.post<{ status: string }>(`/dock_slots/${id}/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dock-schedule"] }),
    onError: (e) => message.error(errorMessage(e)),
  });
  const dayStart = day.startOf("day");
  const pos = (s: Slot) => {
    const from = Math.max(0, dayjs(s.planned_from).diff(dayStart, "minute"));
    const to = Math.min(24 * 60, dayjs(s.planned_to).diff(dayStart, "minute"));
    return { left: `${(from / 1440) * 100}%`, width: `${Math.max(2, ((to - from) / 1440) * 100)}%` };
  };
  const next = (s: Slot) => (s.status === "planned" ? "arrived" : s.status === "arrived" ? "start" : s.status === "in_progress" ? "done" : null);
  const nextLabel: Record<string, string> = { arrived: "Прибыл", start: "Начать", done: "Завершить" };

  return (
    <div>
      <PageHeader
        title="Расписание доков"
        subtitle={`Окна на воротах для поступлений и отгрузок; пересечения на одних воротах запрещены. Время показано по часовому поясу вашего устройства${deviceTimeZone() ? ` (${deviceTimeZone()})` : ""}.`}
        extra={
          <>
            <Button icon={<LeftOutlined />} onClick={() => setDay(day.subtract(1, "day"))} />
            <DatePicker value={day} onChange={(d) => d && setDay(d)} format="DD.MM.YYYY" allowClear={false} />
            <Button icon={<RightOutlined />} onClick={() => setDay(day.add(1, "day"))} />
            <Button onClick={() => setDay(dayjs())}>Сегодня</Button>
            <Link to="/dock_slots">Список слотов</Link>
            {can("inbound.dock_slots.write") && (
              <Button type="primary" onClick={() => navigate("/dock_slots/new")}>
                Новый слот
              </Button>
            )}
          </>
        }
      />
      <Card size="small" loading={schedule.isLoading}>
        {schedule.data && schedule.data.gates.length === 0 && <Empty description="Ворот нет: создайте места хранения с типом «Ворота»" />}
        {schedule.data && schedule.data.gates.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", minWidth: 900 }}>
              <div />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", fontSize: 11, color: "var(--wms-muted)" }}>
                {HOURS.map((h) => (
                  <div key={h} style={{ borderLeft: "1px solid var(--wms-border)", paddingLeft: 2 }}>
                    {h}:00
                  </div>
                ))}
              </div>
              {schedule.data.gates.map((g) => (
                <div key={g.id} style={{ display: "contents" }}>
                  <div style={{ padding: "12px 8px", fontWeight: 600, borderTop: "1px solid var(--wms-border)" }}>
                    <Link to={`/locations/${g.id}`}>{docks && g.number ? `Ворота ${g.number} · ${g.code}` : g.code}</Link>
                  </div>
                  <div style={{ position: "relative", height: 56, borderTop: "1px solid var(--wms-border)", background: "repeating-linear-gradient(90deg, transparent 0, transparent calc(100% / 24 - 1px), var(--wms-grid) calc(100% / 24 - 1px), var(--wms-grid) calc(100% / 24))" }}>
                    {g.slots.map((s) => {
                      const action = next(s);
                      return (
                        <Tooltip
                          key={s.id}
                          title={
                            <div>
                              <div>
                                {dayjs(s.planned_from).format("HH:mm")}–{dayjs(s.planned_to).format("HH:mm")} · {STATUS[s.status]?.label ?? s.status}
                              </div>
                              <div>{s.document_number ? `${s.document_type === "expected_receipt" ? "Поступление" : "Заказ"} ${s.document_number}` : "Без документа"}</div>
                              <div>{[s.vehicle, s.driver].filter(Boolean).join(", ")}</div>
                            </div>
                          }
                        >
                          <div
                            onClick={() => navigate(`/dock_slots/${s.id}`)}
                            style={{ position: "absolute", top: 8, height: 40, ...pos(s), background: s.status === "cancelled" ? "var(--wms-slot-cancelled)" : s.status === "done" ? "var(--wms-slot-done)" : "var(--wms-slot)", border: `1px solid ${s.status === "cancelled" ? "var(--wms-slot-cancelled-border)" : s.status === "done" ? "var(--wms-slot-done-border)" : "var(--wms-slot-border)"}`, borderRadius: 6, padding: "2px 6px", fontSize: 12, overflow: "hidden", whiteSpace: "nowrap", cursor: "pointer" }}
                          >
                            <Tag color={STATUS[s.status]?.color} style={{ marginRight: 4 }}>
                              {STATUS[s.status]?.label ?? s.status}
                            </Tag>
                            {s.document_number ?? s.vehicle ?? "—"}
                            {action && can("inbound.dock_slots.write") && (
                              <Button
                                size="small"
                                type="link"
                                style={{ padding: "0 4px", height: 18 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  act.mutate({ id: s.id, action });
                                }}
                              >
                                {nextLabel[action]}
                              </Button>
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
